import React from 'react'
import { useDispatch } from 'react-redux'

import { ACTION_WEBSOCKET_NEW_MESSAGE } from '@/app/definitions'
import wsEvents from '@/app/store/ws-events-emitter'
import { TWhatsappContent } from '@/app/types'

export type ChatbotChatMessage = {
  // direction: ingress (user -> bot), egress (bot -> user)
  direction: 'ingress' | 'egress'
  id: number
  msg?: TWhatsappContent | null
  campId: number
  subId: number
  contactId: number
  kind: string
  typing: boolean
}

type ChatbotPayload =
  | {
      type: 'newChat'
      campId: number
      subId: number
    }
  | {
      type: 'chat'
      chat: ChatbotChatMessage
    }

/**
 * Subscribes to messages channel (session) over websocket for a given campaign and subscription.
 */
export function useChatbotChannel(campId?: number) {
  const dispatch = useDispatch()
  const [messages, setMessages] = React.useState<ChatbotChatMessage[]>([])
  const [activeSubId, setActiveSubId] = React.useState<number>()
  const [typing, setTyping] = React.useState(false)
  const typingTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTypingTimeout = React.useCallback(() => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current)
      typingTimeoutRef.current = null
    }
  }, [])

  const scheduleTypingTimeout = React.useCallback(
    (isTyping: boolean) => {
      clearTypingTimeout()
      setTyping(isTyping)
      if (!isTyping) {
        return
      }

      setTyping(true)
      typingTimeoutRef.current = setTimeout(() => {
        setTyping(false)
        typingTimeoutRef.current = null
      }, 60000) // 1 minute timeout
    },
    [clearTypingTimeout],
  )

  React.useEffect(() => {
    const handler = (payload: ChatbotPayload) => {
      switch (payload.type) {
        case 'newChat': {
          if ((campId && payload.campId !== campId) || activeSubId) {
            break
          }
          setActiveSubId(payload.subId)
          break
        }
        case 'chat': {
          const { chat } = payload
          if ((campId && chat.campId !== campId) || (activeSubId && chat.subId !== activeSubId)) {
            break
          }

          if (chat.direction === 'egress') {
            scheduleTypingTimeout(chat.typing)
            if (!chat.msg) {
              return // filter out typing indicator messages
            }
          }

          setMessages((prev) => [
            ...prev,
            {
              id: chat.id,
              direction: chat.direction,
              msg: chat.msg,
              campId: chat.campId,
              subId: chat.subId,
              contactId: chat.contactId,
              kind: chat.kind,
              typing: chat.typing,
            },
          ])
          break
        }
        default:
          break
      }
    }
    wsEvents.on('chatbot', handler)
    return () => {
      wsEvents.off('chatbot', handler)
      clearTypingTimeout()
    }
  }, [campId, activeSubId, clearTypingTimeout, scheduleTypingTimeout])

  const send = React.useCallback(
    (text: string) => {
      if (!text.trim() || !activeSubId || !campId) return

      dispatch({
        type: ACTION_WEBSOCKET_NEW_MESSAGE,
        payload: {
          action: 'chat',
          subId: activeSubId,
          msg: {
            message: {
              message: text.trim(), // TODO: support other types
            },
          },
        },
      })
    },
    [activeSubId, campId, dispatch],
  )

  const clear = React.useCallback(() => {
    setActiveSubId(undefined)
    setMessages([])
    setTyping(false)
    clearTypingTimeout()
  }, [clearTypingTimeout])

  return { messages, subId: activeSubId, typing, clear, send }
}
